import React from 'react';
import { GraduationCap, Award, ExternalLink, ShieldCheck } from 'lucide-react';
import { PageBanner } from '../components/Layout';
import Seo from '../components/Seo';
import { IMAGES, EDUCATION, MEMBERSHIPS, PSYPACT_URL, SITE_URL } from '../mock';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'ProfilePage',
  url: `${SITE_URL}/about`,
  mainEntity: {
    '@type': 'Person',
    name: 'Dr. Bill Hoekstra',
    jobTitle: 'Clinical Psychologist, Psy.D. LP DBSM',
    worksFor: { '@type': 'MedicalBusiness', name: 'The Mood and Sleep Clinic, LLC' },
    hasCredential: 'Board-certified Diplomate in Behavioral Sleep Medicine (DBSM)',
    knowsAbout: ['Behavioral Sleep Medicine', 'CBT-I', 'CBT', 'EMDR', 'Accelerated Resolution Therapy', 'Trauma Psychology'],
    memberOf: MEMBERSHIPS.map((m) => ({ '@type': 'Organization', name: m })),
    alumniOf: EDUCATION.map((e) => ({ '@type': 'CollegeOrUniversity', name: e.school })),
  },
};

const trainingAreas = [
  'Behavioral Sleep Medicine',
  'Forensic Psychological Assessment for County Courts',
  'Psychological Assessment',
  'Outpatient crisis settings',
  'Community Mental Health Clinics',
  'Trauma Psychology',
  'EMDR (Eye Movement Desensitization and Reprocessing)',
  'ART (Accelerated Resolution Therapy)',
];

const About = () => {
  return (
    <div>
      <Seo
        title="About Dr. Bill Hoekstra, Psy.D. — Clinical Psychologist"
        description="Dr. Bill Hoekstra, Psy.D., is a licensed clinical psychologist with 24+ years of experience and a board-certified Diplomate in Behavioral Sleep Medicine. PSYPACT authorized across 44 states."
        path="/about"
        schema={aboutSchema}
      />
      <PageBanner title="Dr. Bill Hoekstra, Psy.D. LP DBSM" subtitle="Clinical Psychologist" />

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-10 items-start">
            <div className="md:col-span-2 order-2 md:order-1">
              <p className="text-muted-foreground leading-relaxed">
                I have practiced clinical psychology for 24+ years and am licensed in the state of Minnesota. I am PSYPACT authorized to provide psychological care to patients in 44 states.
              </p>
              <a href={PSYPACT_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-2 text-[#3d6a9c] font-medium hover:text-navy underline">
                View the list of states <ExternalLink size={14} />
              </a>

              <p className="mt-6 text-navy font-heading font-medium">I have extensive training and experience in the following:</p>
              <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                {trainingAreas.map((t) => (
                  <li key={t} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#3d6a9c] shrink-0" /> {t}
                  </li>
                ))}
              </ul>

              <p className="mt-6 text-muted-foreground leading-relaxed">
                I have extensive training in sleep disorder diagnosis and treatment and am a board-certified Diplomate in Behavioral Sleep Medicine (DBSM).
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <div className="inline-flex items-center gap-2 rounded-full bg-secondary text-navy px-4 py-2 text-sm font-heading font-medium">
                  <ShieldCheck size={16} className="text-[#3d6a9c]" />
                  Member — Society of Behavioral Sleep Medicine
                </div>
                <div className="inline-flex items-center gap-2 rounded-full bg-secondary text-navy px-4 py-2 text-sm font-heading font-medium">
                  <ShieldCheck size={16} className="text-[#3d6a9c]" />
                  Member — International Society of ART
                </div>
              </div>
            </div>

            <div className="order-1 md:order-2">
              <div className="rounded-2xl overflow-hidden shadow-xl border-4 border-white">
                <img src="/dr-hoekstra.webp" alt="Dr. Bill Hoekstra, Psy.D., clinical psychologist and founder of The Mood and Sleep Clinic" className="w-full h-[340px] object-cover object-top" width="420" height="340" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Education */}
      <section className="bg-[#f4f8fc] py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="flex items-center gap-2 font-heading text-2xl text-navy">
                <GraduationCap size={24} className="text-[#3d6a9c]" /> Education
              </h2>
              <div className="mt-6 space-y-5">
                {EDUCATION.map((e) => (
                  <div key={e.school} className="border-l-2 border-[#cfe0ef] pl-4">
                    <div className="font-heading font-medium text-navy">{e.school}</div>
                    <div className="text-sm text-muted-foreground">{e.degree}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="flex items-center gap-2 font-heading text-2xl text-navy">
                <Award size={24} className="text-[#3d6a9c]" /> Memberships
              </h2>
              <div className="mt-6 space-y-5">
                {MEMBERSHIPS.map((m) => (
                  <div key={m} className="border-l-2 border-[#cfe0ef] pl-4">
                    <div className="font-heading font-medium text-navy">{m}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
